import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './components/Home';
import About from './components/About';
import Services from './components/Services';
import Projects from './components/Projects';
import Contact from './components/Contact';
import MEPTools from './components/MEPTools';
import './App.css';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const ROUTE_META: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Sustainability Pros — Building Assessment & MEP Consulting | Raleigh-Durham, NC',
    description: 'MEP building assessments, energy and sustainability analysis, and MEP Tools field software — helping owners cut energy use and operating costs. Raleigh-Durham, NC.'
  },
  '/about': {
    title: 'About Us | Sustainability Pros',
    description: 'Field-first MEP practitioners providing building assessments, energy and sustainability analysis, and owner advocacy in the Raleigh-Durham Triangle.'
  },
  '/services': {
    title: 'Building Assessment & MEP Services | Sustainability Pros',
    description: 'Existing building assessment, energy and sustainability analysis, design document review, and commissioning support for owners and facility managers.'
  },
  '/projects': {
    title: 'Projects & Case Studies | Sustainability Pros',
    description: 'Real MEP consulting engagements, including the Fay Street Administration Buildings mechanical assessment in Durham, NC.'
  },
  '/mep-tools': {
    title: 'MEP Tools — Field Software for MEP Professionals | Sustainability Pros',
    description: 'Cloud-native field calculations, facility condition assessment, and energy analysis for MEP professionals — duct leakage testing, airflow calibration, boiler ECM, and more.'
  },
  '/contact': {
    title: 'Contact | Sustainability Pros',
    description: 'Talk to us about a building assessment, energy and sustainability analysis, or MEP Tools. Based in the Raleigh-Durham Triangle, working with clients worldwide.'
  }
};

const App: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const meta = ROUTE_META[location.pathname] ?? ROUTE_META['/'];
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);

    // Report the page view to Google Analytics on every route change.
    window.gtag?.('event', 'page_view', {
      page_path: location.pathname + location.search + location.hash,
      page_location: window.location.href,
      page_title: meta.title
    });

    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/mep-tools" element={<MEPTools />} />
        </Routes>
      </main>
      <Footer />
    </motion.div>
  );
};

export default App;